import { useTranslation } from 'react-i18next'
import { ChangeEvent, useCallback, useState } from 'react'

import LineInput from 'app/common/ui/LineInput'
import { toFixedValueCurrency } from 'app/common/lib/toFixedValue'
import PlainButton from 'app/common/ui-next/plain-button'
import PlainConfirmModal from 'app/common/ui-next/plain-confirm-modal'

import { parseAmount } from '../parseAmount'
import styles from './PersonalLimitEdit.module.scss';


const formID = 'change_personal_limit'

type Props = {
  currency: string
  loading?: boolean
  sending?: boolean
  onSubmit: (value: number) => void
  personalLimit: string | null
  maxOfLimit: number
  exceedError: boolean
  setExceedError: (value: boolean) => void
}

export const PersonalLimitEdit = ({
  currency,
  loading = false,
  sending = false,
  onSubmit,
  personalLimit,
  maxOfLimit,
  exceedError,
  setExceedError,
}: Props) => {
  const { t } = useTranslation('cafeteria')

  const [amount, setAmount] = useState('')
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const handleSubmitConfirmed = useCallback(() => {
    setShowConfirmModal(false)
    onSubmit(parseAmount(amount))
  }, [amount, onSubmit])

  const handleAmountChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget
    if (/^\d+(\.|,)?\d{0,2}$/.test(value) || value === '') {
      setExceedError(false)
      setAmount(value)
    }
  }, [setExceedError])

  const handleValidateAmount = useCallback(() => {
    if (parseAmount(amount) <= maxOfLimit) return true
    setExceedError(true)
    return false
  }, [amount, maxOfLimit, setExceedError])

  const handleSubmit = useCallback((e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault()
    const { id } = e.target
    if (id !== formID) return
    if (handleValidateAmount()) {
      setShowConfirmModal(true)
    }
  }, [handleValidateAmount])

  return (
    <>
      <form className={styles.form} onSubmit={handleSubmit} id={formID}>
        <LineInput
          onChange={handleAmountChange}
          value={amount}
          placeholder={t('settings_limit_personal_set_placeholder', 'Введите сумму')}
          onFocus={() => setExceedError(false)}
          isError={exceedError}
          errorText={t('settings_limit_personal_set_error', {
            maxOfLimit: toFixedValueCurrency(maxOfLimit, false, currency),
          })}
          loading={loading}
          disabled={sending}
        />

        {exceedError && <div className={styles.space} />}

        <PlainButton
          type="submit"
          disabled={loading || sending || !amount}
        >
          {sending
            ? t('settings_limit_personal_set_bt_process', 'Установка\u00A0лимита...')
            : t('settings_limit_personal_set_bt', 'Установить\u00A0лимит')}
        </PlainButton>
      </form>

      {showConfirmModal
        && <PlainConfirmModal
          messageText={
            (personalLimit
              ? t('settings_limit_personal_set_prompt_new', {
                amount: toFixedValueCurrency(amount, false, currency),
              })
              : t('settings_limit_personal_set_prompt', {
                amount: toFixedValueCurrency(amount, false, currency),
              })) ?? ''
          }
          onCancel={() => setShowConfirmModal(false)}
          onConfirm={handleSubmitConfirmed}
          confirmButtonTitle={t('settings_limit_personal_set_confirm', 'Да') || ''}
          cancelButtonTitle={t('settings_limit_personal_set_cancel', 'Отмена') || ''}
        />}
    </>
  )
}
